import { useState, useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import axiosInstance from "../utils/axiosConfig";
import { FaSync, FaDownload, FaPlus, FaEdit, FaTrash, FaChartBar, FaList, FaGraduationCap, FaChalkboardTeacher, FaFileAlt, FaCheckSquare, FaSpinner, FaClock, FaUserGraduate, FaSearch, FaEye, FaSave, FaPlayCircle, FaPauseCircle, FaBullhorn, FaBookOpen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGetFacultyProfileQuery } from "../slices/facultyApiSlice";
import Loader from "../components/Loader";

const FacultyDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("facultyDashboardTab") || "exams";
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Added states similar to InstituteDashboard
  const [selectedExam, setSelectedExam] = useState(null);
  const [examResults, setExamResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [resultsRefreshing, setResultsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmDeleteExamId, setConfirmDeleteExamId] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);

  // Get faculty profile data
  const { data: facultyProfile, isLoading: loadingProfile } = useGetFacultyProfileQuery();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    localStorage.setItem("facultyDashboardTab", activeTab);
  }, [activeTab]);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/faculty/exams");
      if (response.data) {
        setExams(response.data);
      } else {
        // Empty array if no data is returned
        setExams([]);
      }
      // Always clear any previous errors on successful request
      setError(null);
    } catch (error) {
      console.error("Error fetching exams:", error);
      // If 404 "No uploads found", treat it as empty array instead of error
      if (error.response?.status === 404) {
        setExams([]);
        setError(null);
      } else {
        toast.error(error.response?.data?.message || "Failed to fetch exams");
        setError(error.response?.data?.message || "Failed to fetch exams");
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshExams = async () => {
    try {
      setIsRefreshing(true);
      await fetchExams();
      toast.success('Exams refreshed successfully');
    } catch (error) {
      console.error("Error refreshing exams:", error);
      toast.error("Failed to refresh exams");
    } finally {
      setIsRefreshing(false);
    }
  };

  // New functions from InstituteDashboard
  const handleViewResults = async (examId) => {
    try {
      setResultsLoading(true);
      
      // Find the exam
      const exam = exams.find(e => e._id === examId);
      if (!exam) {
        toast.error('Exam not found');
        return;
      }
      
      setSelectedExam(exam);
      
      // Get the exam results
      const response = await axiosInstance.get(`/api/exams/results/${examId}`);
      
      if (response.data) {
        setExamResults(response.data);
        setShowResultsModal(true);
      }
    } catch (error) {
      console.error("Error fetching results:", error);
      toast.error(error.response?.data?.message || "Failed to fetch results");
    } finally {
      setResultsLoading(false);
    }
  };

  const handleResultsRefresh = async () => {
    if (!selectedExam) return;
    
    try {
      setResultsRefreshing(true);
      // Use the same endpoint as handleViewResults for consistency
      const response = await axiosInstance.get(`/api/exams/results/${selectedExam?._id}`);
      
      if (response.data) {
        setExamResults(response.data);
        toast.success('Results refreshed successfully');
      }
    } catch (error) {
      console.error("Error refreshing results:", error);
      toast.error(error.response?.data?.message || "Failed to refresh results");
    } finally {
      setResultsRefreshing(false);
    }
  };

  const downloadResultsAsCSV = () => {
    if (!selectedExam) {
      toast.error('No exam selected');
      return;
    }
    
    if (!examResults.length) {
      toast.error('No results to download');
      return;
    }

    // Create CSV headers
    const headers = ['Student Name', 'Email', 'Score (%)', 'Correct Answers', 'Total Questions', 'Result', 'Time Taken', 'Submission Date'];
    
    // Get the passing percentage from the selected exam (default to 60 if not available)
    const passingPercentage = selectedExam?.passingPercentage || 60;
    
    // Convert results to CSV format with properly formatted date
    const csvData = examResults.map(result => {
      // Format the date properly
      const submissionDate = result.submittedAt 
        ? new Date(result.submittedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        : 'N/A';
      
      // Format time taken
      const timeTaken = result.timeTaken !== undefined && result.timeTaken !== null
        ? `${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`
        : 'N/A';

      // Check if student failed (score below passing percentage)
      const hasFailed = (Number(result.score) || 0) < passingPercentage;
      
      return {
        data: [
          result.student?.name || 'Unknown',
          result.student?.email || 'No email',
          (Number(result.score) || 0).toFixed(1),
          result.correctAnswers || '0',
          result.totalQuestions || '0',
          hasFailed ? 'FAIL' : 'PASS',
          timeTaken,
          submissionDate
        ],
        hasFailed
      };
    });

    // For Excel, we'll use a different approach - create an HTML table that Excel can interpret
    let htmlTable = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
    htmlTable += '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Exam Results</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>';
    htmlTable += '<body><table>';
    
    // Add header row
    htmlTable += '<tr>';
    headers.forEach(header => {
      htmlTable += `<th style="background-color:#E0E0E0; font-weight:bold;">${header}</th>`;
    });
    htmlTable += '</tr>';
    
    // Add data rows
    csvData.forEach(row => {
      if (row.hasFailed) {
        // Red background for failed students
        htmlTable += '<tr>';
        row.data.forEach(cell => {
          htmlTable += `<td style="background-color:#FFCCCC;">${cell}</td>`;
        });
        htmlTable += '</tr>';
      } else {
        // Normal background for passed students
        htmlTable += '<tr>';
        row.data.forEach(cell => {
          htmlTable += `<td>${cell}</td>`;
        });
        htmlTable += '</tr>';
      }
    });
    
    htmlTable += '</table></body></html>';
    
    // Create blob and download
    const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Create filename with current date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    
    link.setAttribute('href', url);
    // Safely access examName with optional chaining
    link.setAttribute('download', `${selectedExam?.examName || 'exam'}_results_${currentDate}.xls`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleToggleExamMode = async (examId) => {
    const currentExam = exams.find((exam) => exam._id === examId);

    // Check if the exam is approved
    if (!currentExam || currentExam.status !== "approved") {
      toast.error("Exam mode can only be toggled for approved exams.");
      return;
    }

    try {
      setToggleLoading(true);
      const newExamMode = !currentExam.examMode; // Toggle the current state

      const response = await axiosInstance.put(
        `/api/exams/${examId}/exam-mode`,
        {
          examMode: newExamMode
        }
      );

      // Update the exams state to reflect the new exam mode
      setExams((prevExams) =>
        prevExams.map((exam) =>
          exam._id === examId ? { ...exam, examMode: newExamMode } : exam
        )
      );

      toast.success(response.data.message || `Exam mode ${newExamMode ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error("Error toggling exam mode:", error);
      toast.error(error.response?.data?.message || "Failed to toggle exam mode");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleReleaseResults = async (examId) => {
    if (!window.confirm('Are you sure you want to release the results to students? This action cannot be undone.')) {
      return;
    }
    
    try {
      setReleaseLoading(true);
      
      const response = await axiosInstance.post(`/api/exams/release/${examId}`);
      
      // Update the exams list
      setExams(exams.map(exam => 
        exam._id === examId ? { ...exam, resultsReleased: true } : exam
      ));
      
      toast.success(response.data?.message || 'Results released to students successfully');
      
      // If the modal is open and showing this exam's results, refresh them
      if (selectedExam?._id === examId) {
        const resultsResponse = await axiosInstance.get(`/api/exams/results/${examId}`);
        setExamResults(resultsResponse.data);
      }
    } catch (error) {
      console.error("Error releasing results:", error);
      toast.error(error.response?.data?.message || "Failed to release results");
    } finally {
      setReleaseLoading(false);
    }
  };

  const confirmDeleteExam = (exam) => {
    setConfirmDeleteExamId(exam._id);
    setShowDeleteModal(true);
  };

  const handleDeleteExam = async () => {
    if (!confirmDeleteExamId) return;
    
    try {
      setLoading(true);
      
      // Delete exam
      const response = await axiosInstance.delete(`/api/faculty/exams/${confirmDeleteExamId}`);
      
      // Update the exams list
      setExams(exams.filter(e => e._id !== confirmDeleteExamId));
      
      toast.success(response?.data?.message || 'Exam deleted successfully');
      
      setShowDeleteModal(false);
      setConfirmDeleteExamId(null);
    } catch (error) {
      console.error("Error deleting exam:", error);
      toast.error(error.response?.data?.message || "Failed to delete exam");
    } finally {
      setLoading(false);
    }
  };

  // Get filtered exams based on search term and status filter
  const getFilteredExams = () => {
    return exams
      .filter(exam => 
        exam.examName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.subject?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter(exam => {
        if (statusFilter === 'all') return true;
        return exam.status === statusFilter;
      });
  };

  // Status badge color mapping
  const getStatusBadgeClass = (status) => {
    if (isDarkMode) {
      switch (status) {
        case 'approved':
          return 'bg-green-400/10 text-green-400';
        case 'rejected':
          return 'bg-red-400/10 text-red-400';
        case 'pending':
        default:
          return 'bg-yellow-400/10 text-yellow-400';
      }
    } else {
      switch (status) {
        case 'approved':
          return 'bg-green-100 text-green-700';
        case 'rejected':
          return 'bg-red-100 text-red-700';
        case 'pending':
        default:
          return 'bg-yellow-100 text-yellow-700';
      }
    }
  };

  const handleCreateExam = () => {
    navigate('/institute/exams/create', { state: { fromFaculty: true } });
  };

  const handleEditExam = (examId) => {
    navigate(`/faculty/exams/edit/${examId}`);
  };

  const handleViewExam = (examId) => {
    navigate(`/faculty/exams/view/${examId}`);
  };

  const renderExamsTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Dashboard Header */}
      <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gradient-to-r from-blue-900/40 to-indigo-900/40' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Faculty Dashboard
            </h1>
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {loadingProfile ? 'Loading your profile...' : `Welcome back, ${facultyProfile?.name || 'Faculty'}!`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className={`relative ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search exams..."
                className={`pl-10 pr-4 py-2 rounded-lg text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } border focus:outline-none focus:ring-2 ${
                  isDarkMode ? 'focus:ring-violet-500/50' : 'focus:ring-violet-500/50'
                }`}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-700 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } border focus:outline-none focus:ring-2 ${
                isDarkMode ? 'focus:ring-violet-500/50' : 'focus:ring-violet-500/50'
              }`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={handleCreateExam}
              className={`px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2 transform transition-all duration-200 hover:scale-105 ${
                isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20'
              }`}
            >
              <FaPlus className="w-4 h-4" />
              Create New Exam
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Exams</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {exams.length}
              </h3>
            </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
              <FaFileAlt className="w-6 h-6" />
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Approved Exams</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {exams.filter(exam => exam.status === 'approved').length}
              </h3>
            </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
              <FaCheckSquare className="w-6 h-6" />
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pending Exams</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {exams.filter(exam => exam.status === 'pending').length}
              </h3>
            </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>
              <FaClock className="w-6 h-6" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Exams Section */}
      <div className={`rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}">
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Your Uploaded Exams
          </h2>
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            {/* Search input */}
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search exams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 text-white border-gray-600 focus:border-violet-500' 
                    : 'bg-white text-gray-900 border-gray-300 focus:border-violet-500'
                } border focus:outline-none`}
              />
              <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  ×
                </button>
              )}
            </div>
            <button
              onClick={refreshExams}
              disabled={isRefreshing}
              className={`whitespace-nowrap p-2 rounded-lg font-medium inline-flex items-center justify-center transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
              title="Refresh Exams"
            >
              <FaSync className={isRefreshing ? 'animate-spin' : ''} />
              <span className="ml-2 text-sm">Refresh</span>
            </button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {(loading || isRefreshing) ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader size="medium" text={isRefreshing ? 'Refreshing exams...' : 'Loading exams...'} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className={`text-sm text-red-500`}>{error}</p>
              <button 
                onClick={refreshExams}
                className={`mt-4 px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Try Again
              </button>
            </div>
          ) : getFilteredExams().length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              {searchTerm ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No exams found matching "{searchTerm}".
                </p>
              ) : (
                <>
                  <div className={`p-3 rounded-full mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <FaFileAlt className={`text-3xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    No Exams Uploaded Yet
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} max-w-md text-center mb-4`}>
                    You haven't uploaded any exams yet. Create your first exam to get started.
                  </p>
                  <button
                    onClick={handleCreateExam}
                    className={`px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2 ${
                      isDarkMode
                        ? 'bg-violet-600 hover:bg-violet-500 text-white' 
                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                    }`}
                  >
                    <FaPlus className="w-4 h-4" />
                    Create First Exam
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed">
                <thead className="sticky top-0 z-10">
                  <tr className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-1/5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Exam Name
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-1/6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Description
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Status
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Created
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[12%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Results
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[12%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Actions
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Mode
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[10%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Toggle
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredExams().map((exam, index) => (
                    <tr 
                      key={exam._id} 
                      className={`${
                        isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                      } ${
                        index !== getFilteredExams().length - 1 ? `border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}` : ''
                      }`}
                    >
                      <td className={`px-6 py-4 text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {exam.examName}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div 
                          className="max-w-[200px] overflow-hidden"
                          title={exam.description || 'No description'}
                        >
                          <p className="truncate">
                            {exam.description || 'No description'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          exam.status === 'approved'
                            ? isDarkMode
                              ? 'bg-green-400/10 text-green-400' 
                              : 'bg-green-100 text-green-700'
                            : isDarkMode
                              ? 'bg-yellow-400/10 text-yellow-400'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {exam.status}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {new Date(exam.createdAt).toLocaleDateString('en-GB')}
                      </td>
                      <td className="px-6 py-4">
                        {exam.status === "approved" ? (
                          <button
                            onClick={() => handleViewResults(exam._id)}
                            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                              isDarkMode
                                ? 'text-violet-400 hover:bg-violet-400/10'
                                : 'text-violet-600 hover:bg-violet-50'
                            }`}
                          >
                            View Results
                          </button>
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Pending Approval
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {exam.status === "approved" ? (
                          !exam.resultsReleased ? (
                            <button
                              onClick={() => handleReleaseResults(exam._id)}
                              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-green-400 hover:bg-green-400/10'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                            >
                              Release Results
                            </button>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isDarkMode ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-700'
                            }`}>
                              Results Released
                            </span>
                          )
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Pending Approval
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          exam.examMode
                            ? isDarkMode
                              ? 'bg-green-400/10 text-green-400' 
                              : 'bg-green-100 text-green-700'
                            : isDarkMode
                              ? 'bg-red-400/10 text-red-400'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {exam.examMode ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleExamMode(exam._id)}
                          disabled={exam.status !== "approved"}
                          className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            exam.examMode
                              ? isDarkMode
                                ? 'text-red-400 hover:bg-red-400/10'
                                : 'text-red-600 hover:bg-red-50'
                              : isDarkMode
                                ? 'text-green-400 hover:bg-green-400/10'
                                : 'text-green-600 hover:bg-green-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {exam.examMode ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => confirmDeleteExam(exam)}
                          className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isDarkMode
                              ? 'text-red-400 hover:bg-red-400/10'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                        >
                          <FaTrash className="mr-1" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderResultsModal = () => {
    if (!showResultsModal || !selectedExam) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
          </div>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal Header - Fixed */}
            <div className={`px-4 pt-5 pb-4 sm:px-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className={`text-lg leading-6 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedExam?.examName || 'Exam'} - Results
                  </h3>
                  <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Exam Results Overview
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleResultsRefresh}
                    className={`p-2 rounded-lg ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <FaSync className={resultsRefreshing ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={downloadResultsAsCSV}
                    className={`p-2 rounded-lg ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <FaDownload />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-h-[60vh] overflow-y-auto p-6`}>
              {resultsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader size="medium" />
                </div>
              ) : examResults.length > 0 ? (
                <div>
                  {/* Results Summary */}
                  <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Results Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {(() => {
                        const passingPercentage = selectedExam?.passingPercentage || 60;
                        const totalStudents = examResults.length;
                        const passedStudents = examResults.filter(r => (Number(r.score) || 0) >= passingPercentage).length;
                        const failedStudents = totalStudents - passedStudents;
                        const passRate = totalStudents > 0 ? (passedStudents / totalStudents * 100).toFixed(1) : 0;
                        
                        return (
                          <>
                            <div>
                              <div className="text-sm text-gray-500">Total Students</div>
                              <div className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {totalStudents}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">Passed</div>
                              <div className={`text-xl font-semibold text-green-500`}>
                                {passedStudents} ({passRate}%)
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">Failed</div>
                              <div className={`text-xl font-semibold text-red-500`}>
                                {failedStudents} ({(100 - passRate).toFixed(1)}%)
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <div className="text-sm">
                      <span className="font-medium">Passing Score: </span>
                      <span>{selectedExam?.passingPercentage || 60}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'}`}></div>
                      <span className="text-sm">Failed</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Score
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Correct/Total
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time Taken
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted At
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {examResults.map((result) => {
                          // Get the passing percentage (default to 60 if not available)
                          const passingPercentage = selectedExam?.passingPercentage || 60;
                          // Check if student failed
                          const hasFailed = (Number(result.score) || 0) < passingPercentage;
                          
                          return (
                            <tr 
                              key={result._id}
                              className={`${hasFailed ? 
                                (isDarkMode ? 'bg-red-900/30' : 'bg-red-100') : ''
                              }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                  {result.student?.name || 'Anonymous'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {result.student?.email || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  result.score >= 70
                                    ? isDarkMode ? 'bg-green-100/10 text-green-400' : 'bg-green-100 text-green-800'
                                    : result.score >= passingPercentage
                                      ? isDarkMode ? 'bg-yellow-100/10 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                                      : isDarkMode ? 'bg-red-100/10 text-red-400' : 'bg-red-100 text-red-800'
                                }`}>
                                  {(Number(result.score) || 0).toFixed(1)}%
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {result.correctAnswers || 0}/{result.totalQuestions || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {result.timeTaken !== undefined && result.timeTaken !== null
                                  ? `${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`
                                  : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {new Date(result.submittedAt).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  No results available for this exam yet.
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className={`px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse ${
              isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'
            }`}>
              <button
                type="button"
                onClick={() => setShowResultsModal(false)}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Close
              </button>
              {selectedExam && selectedExam.status === "approved" && !selectedExam.resultsReleased && (
                <button
                  type="button"
                  onClick={() => handleReleaseResults(selectedExam._id)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Release Results
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteConfirmationModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-black' : 'bg-gray-500'} opacity-75`}></div>
        </div>

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`${
            isDarkMode 
              ? 'bg-[#1a1f2e] text-white border border-gray-700' 
              : 'bg-white text-gray-900 border border-gray-300'
          } rounded-lg w-full sm:max-w-md shadow-xl relative mx-auto`}
        >
          <div className={`p-6`}>
            <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Confirm Deletion
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to delete this exam? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteExam}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20' 
                    : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'
                }`}
              >
                Delete
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 pt-20 pb-12">
        {renderExamsTab()}
        {showDeleteModal && renderDeleteConfirmationModal()}
        {showResultsModal && renderResultsModal()}
      </div>
    </div>
  );
};

export default FacultyDashboard; 